import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import TabsComponent from '@/components/TabsComponent/TabsComponent';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import {
  stateTabs, administration, fieldsTabsToBackKey, fieldsTabsToReduxState, fieldsTabsToIndex, helpTabsToIndex, adminPagesWithTabs,
  helpTabsToReduxState,
} from '@/helpers/constants';
import { tabLowerCase } from '@/helpers/utils';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { getForms } from '@/redux/Forms/forms.reducer';
import { getHelp } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { HelpTextState, ReduxForms, ReduxFormsState, ReduxHelp } from '@/types';
import styles from './AdministrationHeader.module.scss';

type AdministrationHeaderProps = {
  help: HelpTextState;
  forms: ReduxFormsState;
  tab: string;
  windowLocationPathName?: string;
};

const AdministrationHeader: React.FC<AdministrationHeaderProps> = (props) => {
  const { help, forms, tab, windowLocationPathName = window.location.pathname } = props;
  const { t } = useTranslation();
  const subTab = tabLowerCase(windowLocationPathName.split('/')[4]);
  const { goTo } = useGoTo();
  const [isSpinner, setIsSpinner] = useState(false);
  const { addToast } = useToastContext();
  const isFieldsPage = windowLocationPathName.toLowerCase().includes('/administration/fields');
  const isHelpTextPage = windowLocationPathName.toLowerCase().includes('/administration/help');
  const pageWithoutTabs = !adminPagesWithTabs.includes(tab);
  const tabKeyBackend = fieldsTabsToBackKey?.[subTab];
  const tabReduxState = isFieldsPage ? fieldsTabsToReduxState?.[subTab] : helpTabsToReduxState[subTab];

  const saveHelp = async () => {
    const endPoint = subTab === 'description' ? 'description' : stateTabs[subTab];
    setIsSpinner(true);
    delete (help[endPoint] as ReduxHelp)?.modified;
    administrationServices.saveHelp(help[endPoint], subTab)
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setIsSpinner(false));
  };

  const saveFieldsMaintenance = async () => {
    if ((forms?.[tabReduxState] as ReduxForms)?.modified) {
      setIsSpinner(true);
      const toSave = {
        form: forms?.[tabReduxState],
      };
      delete (toSave.form as ReduxForms).modified;
      administrationServices.saveFieldMaintenanceTabForms(tabKeyBackend, toSave)
        .catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setIsSpinner(false));
    }
  };

  const handleTabChange = (path) => {
    if (isFieldsPage) saveFieldsMaintenance();
    goTo(path);
  };

  const tabsValues = (selectedTab) => helpTabsToIndex[selectedTab] || helpTabsToIndex.description;
  const fieldsValues = (selectedTab) => fieldsTabsToIndex[selectedTab] || fieldsTabsToIndex.description;
  const checkSaveDisabled = () => {
    const reduxState = isFieldsPage ? forms : help;

    return !(reduxState?.[tabReduxState] as ReduxForms)?.modified;
  };

  const handleClickSave = () => {
    if (isHelpTextPage) {
      saveHelp();
    } else if (isFieldsPage) {
      saveFieldsMaintenance();
    }
  };

  const SaveButton = () => {
    const isSaveDisabled = checkSaveDisabled();

    return (
      <div className={styles.ButtonSave}>
        {isSpinner && (<div className={styles.SpinnerHeader}><Spinner small isVisible /></div>)}
        {!isSpinner && (
          <IcoButtonSimple
            id="btn-save-campaign"
            icon="icon-diskette-speichern"
            handleClick={handleClickSave}
            disabled={isSaveDisabled}
            tooltipText={t('campaign.save') || ''}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.AdministrationHeader} ${pageWithoutTabs ? styles.HeaderWithoutTabs : ''}`}>
      <div className={styles.HeaderTitle}>{t(administration[windowLocationPathName.toLowerCase()])}</div>
      {(isHelpTextPage || isFieldsPage) && (
        <div className={styles.TabHeader}>
          <TabsComponent
            key={subTab}
            isFields={isFieldsPage}
            isHelp={isHelpTextPage}
            handleNavClick={handleTabChange}
            tabSelected={isFieldsPage ? fieldsValues(subTab) : tabsValues(subTab)}
          />
          {SaveButton()}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  help: getHelp(state),
  forms: getForms(state),
});

export default connect(mapStateToProps)(AdministrationHeader);
